import React, {
  useMemo, useState, useRef, useEffect, useCallback,
} from 'react';
import {
  ScrollView, Text, Box, VStack,
} from '@gluestack-ui/themed';
import { useForm, FormProvider, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { useDialog } from '@/common/context/DialogContext';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import { useEvaluationGradingConfig } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';
import log from '@/common/utils/logger';
import {
  getGetModuleSubmissionEvaluationQueryKey,
  useRegenerateModuleFeedback,
} from '@/common/api/endpoints/submission-evaluation/submission-evaluation';
import type { Task } from '@/common/api/models/task';
import { collectMissingEvalRatings } from '../utils/collectMissingEvalRatings';
import GradingForm from './GradingForm';
import SubmissionElement from './SubmissionElement/SubmissionElement';
import { TaskGroupDimensionHeader, taskRowDimensionKeys } from './SubmissionElement/ElementHeader';
import { isOralExamTask, resolveModuleGradingFormDefaults } from '../utils/evaluationFormMapping';
import type { EvaluationFormProps, EvaluationFormData } from '../types';

/**
 * Main form for evaluating a module submission.
 * Handles module-level grading and coordinates task-level evaluations.
 */
const EvaluationForm: React.FC<EvaluationFormProps> = ({
  tasks,
  taskEvaluations,
  eventKinds = [],
  selectedModuleSubmission,
  isReadOnly,
  grading,
  onRequestSave,
  hasActiveTaskFilter = false,
  isAutograding = false,
  moduleSubmissionId,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const regenerateModuleFeedbackMutation = useRegenerateModuleFeedback();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Auto-expand the single task when there is only one, for convenience
  const [taskExpanded, setTaskExpanded] = useState<Record<string, boolean>>(() => {
    if (tasks.length === 1 && tasks[0]?.id) {
      return { [tasks[0].id]: true };
    }
    return {};
  });
  const { showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const { evalDimensionKeys, oralExamDimensionKeys } = useEvaluationGradingConfig();
  const scrollRef = useRef<React.ComponentRef<typeof ScrollView>>(null);

  // Default feedback proposed when the lecturer hasn't entered any: pick a
  // task-aware message so an oral-exam-only module points the student to the
  // detailed per-question feedback instead of a generic placeholder.
  const defaultModuleFeedback = useMemo(() => {
    const hasOralExam = tasks.some(isOralExamTask);
    return hasOralExam
      ? t('lecturer.evalDesk.defaultModuleFeedbackOralExam')
      : t('lecturer.evalDesk.defaultModuleFeedback');
  }, [tasks, t]);

  // Track submission key to prevent resetting during submission
  const submissionKeyRef = useRef(`${selectedModuleSubmission.userId}-${selectedModuleSubmission.moduleId}`);
  const prevIsAutogradingRef = useRef(isAutograding);

  const gradingDefaults = useMemo(
    () => resolveModuleGradingFormDefaults(grading, defaultModuleFeedback, { finalized: isReadOnly }),
    [grading, defaultModuleFeedback, isReadOnly],
  );

  const methods = useForm<EvaluationFormData>({
    defaultValues: {
      taskEvaluations,
      grading: gradingDefaults,
    },
  });

  const {
    handleSubmit, reset, getValues, setValue, formState: { dirtyFields },
  } = methods;

  const moduleEvaluationQueryKey = useMemo(
    () => getGetModuleSubmissionEvaluationQueryKey(
      selectedModuleSubmission.userId,
      selectedModuleSubmission.courseId,
      selectedModuleSubmission.moduleId,
    ),
    [
      selectedModuleSubmission.userId,
      selectedModuleSubmission.courseId,
      selectedModuleSubmission.moduleId,
    ],
  );

  const handleRegenerateModuleFeedback = useCallback(async () => {
    try {
      const response = await regenerateModuleFeedbackMutation.mutateAsync({
        userId: selectedModuleSubmission.userId,
        courseId: selectedModuleSubmission.courseId,
        moduleId: selectedModuleSubmission.moduleId,
      });

      const newGrading = response?.data?.data?.grading;
      if (newGrading) {
        setValue('grading.feedback', newGrading.feedback, { shouldDirty: true });
      }

      await queryClient.invalidateQueries({ queryKey: moduleEvaluationQueryKey });
      showToastMessage('success', t('lecturer.evalDesk.regenerateSuccess'));
    } catch (error) {
      showToastMessage('error', t('lecturer.evalDesk.regenerateError'));
      log.error(error);
    }
  }, [
    moduleEvaluationQueryKey,
    queryClient,
    regenerateModuleFeedbackMutation,
    selectedModuleSubmission.courseId,
    selectedModuleSubmission.moduleId,
    selectedModuleSubmission.userId,
    setValue,
    showToastMessage,
    t,
  ]);

  // RHF reset policy: only on student/module identity change, or when autograde
  // finishes (server overwrote task + module fields). Late-arriving module grading
  // patches grading.* via setValue when those fields are not dirty — never reset
  // taskEvaluations from derived cache identity churn after per-task autosave.
  useEffect(() => {
    const currentKey = `${selectedModuleSubmission.userId}-${selectedModuleSubmission.moduleId}`;
    const wasAutograding = prevIsAutogradingRef.current;
    prevIsAutogradingRef.current = isAutograding;

    // Autograde finished: server overwrote task + module feedback — reload everything.
    if (wasAutograding && !isAutograding) {
      reset(
        {
          taskEvaluations,
          grading: resolveModuleGradingFormDefaults(grading, defaultModuleFeedback, { finalized: isReadOnly }),
        },
        {
          keepDirtyValues: false,
          keepErrors: false,
        },
      );
      return;
    }

    // If submission changed (different student/module), always reset
    if (currentKey !== submissionKeyRef.current) {
      submissionKeyRef.current = currentKey;
      reset(
        {
          taskEvaluations,
          grading: resolveModuleGradingFormDefaults(grading, defaultModuleFeedback, { finalized: isReadOnly }),
        },
        {
          keepDirtyValues: false,
          keepErrors: false,
        },
      );
      return;
    }

    if (isSubmitting || isAutograding) return;

    const gradingFormDefaults = resolveModuleGradingFormDefaults(
      grading,
      defaultModuleFeedback,
      { finalized: isReadOnly },
    );
    const currentAutoCalculate = getValues('grading.autoCalculateGrade') as boolean | undefined;
    const currentGrade = getValues('grading.grade') as string | undefined;
    const resolvedAutoCalculate = currentAutoCalculate ?? gradingFormDefaults.autoCalculateGrade;
    const preserveComputedGrade = resolvedAutoCalculate && !!currentGrade;

    if (!dirtyFields.grading?.feedback) {
      setValue('grading.feedback', gradingFormDefaults.feedback, { shouldDirty: false });
    }
    if (!dirtyFields.grading?.autoCalculateGrade) {
      setValue('grading.autoCalculateGrade', resolvedAutoCalculate, { shouldDirty: false });
    }
    if (!dirtyFields.grading?.grade) {
      setValue(
        'grading.grade',
        preserveComputedGrade ? currentGrade : gradingFormDefaults.grade,
        { shouldDirty: false },
      );
    }
  }, [
    grading,
    selectedModuleSubmission.userId,
    selectedModuleSubmission.moduleId,
    isSubmitting,
    isAutograding,
    isReadOnly,
    reset,
    setValue,
    getValues,
    defaultModuleFeedback,
    taskEvaluations,
    dirtyFields.grading?.feedback,
    dirtyFields.grading?.grade,
    dirtyFields.grading?.autoCalculateGrade,
  ]);

  const tasksByLevel = useMemo(
    () => tasks.reduce((acc: Record<number, Task[]>, task) => {
      const { level = 1 } = task;
      if (!acc[level]) acc[level] = [];
      acc[level].push(task);
      return acc;
    }, {}),
    [tasks],
  );

  const onFormError = (errs: FieldErrors<EvaluationFormData>) => {
    const tasksWithErrors = Object.keys(errs?.taskEvaluations || {}).reduce((acc: Record<string, boolean>, taskId) => {
      acc[taskId] = true;
      return acc;
    }, {});

    if (Object.keys(tasksWithErrors).length > 0) {
      setTaskExpanded((prev) => ({ ...prev, ...tasksWithErrors }));
    }

    showToastMessage(
      'error',
      'Cannot submit evaluation, please check for incomplete task or unset evaluations/module grade.',
      'Evaluation Incomplete',
    );
    log.info('Form validation errors:', errs);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await handleSubmit(async (data) => {
        setTaskExpanded({});
        await onRequestSave(data, false);
        // Success - reset form state to current values to clear dirty state without wiping input
        const currentValues = getValues();
        reset(currentValues, { keepValues: true });
      }, onFormError)();
    } catch (error) {
      showToastMessage('error', 'Failed to save evaluation');
      log.info('Save failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitToStudent = async () => {
    const autoCalculateGrade = getValues('grading.autoCalculateGrade') as boolean | undefined;
    const moduleGrade = getValues('grading.grade') as string | number | undefined;
    const hasOverrideWithGrade = autoCalculateGrade === false
      && moduleGrade != null
      && moduleGrade !== '';

    if (!hasOverrideWithGrade) {
      const gaps = collectMissingEvalRatings(
        tasks,
        getValues('taskEvaluations'),
        eventKinds,
        evalDimensionKeys,
      );
      if (gaps.length > 0) {
        const firstGap = gaps[0];
        setTaskExpanded((prev) => ({ ...prev, [firstGap.taskId]: true }));
        showToastMessage(
          'error',
          t('lecturer.evalDesk.incompleteSubmitMessage', {
            taskTitle: firstGap.taskTitle,
            dimension: firstGap.missingDimensions[0],
          }),
          t('lecturer.evalDesk.incompleteSubmitTitle'),
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await handleSubmit(async (data) => {
        setTaskExpanded({});
        await onRequestSave(data, true);
      }, onFormError)();
      // Success - will navigate away, no need to reset
    } catch (error) {
      reportApiError(error, t('lecturer.evalDesk.submitErrorContext'), 'toast', {
        expectedErrorCodes: ['INVALID_STATE'],
      });
      log.info('Submit failed:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box flex={1} bg="$bgApp" testID="evaluation-form">
        <ScrollView flex={1} ref={scrollRef}>
          <Box px="$6" py="$6">
            {/* No global dimension column header: tasks of different types
                (essay, MCQ, oral exam) use different dimension sets, so a
                single header would mislabel rows. Each row carries its own
                criteria via the per-task harvey-ball grid. */}

            {tasks.length === 0 && (
              <Box
                py="$6"
                px="$4"
                borderWidth={1}
                borderColor="$borderLight"
                borderRadius="$md"
                bg="$bgSubtle"
              >
                <Text size="sm" color="$textBody" mb="$1">
                  {t('lecturer.evalDesk.noTasksMatchFilter')}
                </Text>
                {hasActiveTaskFilter && (
                  <Text size="xs" color="$textSubtle">
                    {t('lecturer.evalDesk.noTasksMatchFilterHint')}
                  </Text>
                )}
              </Box>
            )}

            <VStack space="lg">
              {Object.entries(tasksByLevel).sort().map(([level, levelTasks]) => (
                <VStack key={level} space="xs">
                  {/* Level header */}
                  <Text
                    fontWeight="bold"
                    color="$textSubtle"
                    size="xs"
                    textTransform="uppercase"
                    letterSpacing="$md"
                    mt="$2"
                    mb="$1"
                  >
                    Level
                    {' '}
                    {level}
                  </Text>

                  {/* Task rows - simple list without card wrappers.
                      A small dimension column header is injected only when
                      the dim signature changes between consecutive tasks
                      (e.g. essay block -> oral exam block). */}
                  <Box borderTopWidth={1} borderColor="$borderLight">
                    {(() => {
                      let prevSignature: string | null = null;
                      return levelTasks.map((task) => {
                        const dimKeys = taskRowDimensionKeys(
                          task,
                          evalDimensionKeys,
                          oralExamDimensionKeys,
                        );
                        const signature = dimKeys.join('|');
                        const showHeader = signature !== prevSignature;
                        prevSignature = signature;
                        return (
                          <React.Fragment
                            key={`submission-${selectedModuleSubmission.userId}-${selectedModuleSubmission.moduleId}-task-${task.id}`}
                          >
                            {showHeader && (
                              <TaskGroupDimensionHeader dimensionKeys={dimKeys} />
                            )}
                            <Box borderBottomWidth={1} borderColor="$borderLight">
                              <SubmissionElement
                                task={task}
                                eventKinds={eventKinds}
                                selectedModuleSubmission={selectedModuleSubmission}
                                isExpanded={!!taskExpanded[task.id]}
                                isDisabled={isSubmitting || isAutograding}
                                isReadOnly={isReadOnly}
                                isAutograding={isAutograding}
                                toggleElement={() => {
                                  setTaskExpanded((prev) => ({ ...prev, [task.id]: !prev[task.id] }));
                                }}
                              />
                            </Box>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </Box>
                </VStack>
              ))}
            </VStack>

            {/* Footer: Grade + Feedback + Actions */}
            <Box mt="$4">
              <GradingForm
                key={`grading-user-${selectedModuleSubmission.userId}-module-${selectedModuleSubmission.moduleId}`}
                tasks={tasks}
                eventKinds={eventKinds}
                moduleSubmissionId={moduleSubmissionId}
                isDisabled={isSubmitting || isAutograding || regenerateModuleFeedbackMutation.isPending}
                isReadOnly={isReadOnly}
                isRegenerating={regenerateModuleFeedbackMutation.isPending}
                onRegenerate={handleRegenerateModuleFeedback}
                onSave={handleSave}
                onSend={handleSubmitToStudent}
              />
            </Box>
          </Box>
        </ScrollView>
      </Box>
    </FormProvider>
  );
};

export default EvaluationForm;
